import type {
	OrderSampleAttachmentType,
	OrderSampleStatus,
	UpdateSampleStatusInput,
} from '@codezo/graphql';
import {
	OrderSampleChoice,
	SampleActionType,
	useUpdateSampleStatusMutation,
	useApproveNewSampleRoundMutation,
	useSetSamplePaymentTreatmentMutation,
	useGetShippingCarriersQuery,
	SamplePaymentTreatment,
} from '@codezo/graphql';
import { FileText as FileTextIcon, Trash2, TriangleAlert, Upload } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useSampleAttachmentUpload } from '../../hooks/useSampleAttachmentUpload';
import { FileInput } from '../ui/file-input';
import { Button } from '../ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../ui/dialog';
import { DatePicker } from '../ui/date-picker';
import { Field } from '../ui/field';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { SampleTreatmentPicker } from '../SampleTreatmentPicker';
import { getMutationError } from '../../lib/mutation-response';
import { SAMPLE_MEDIA_ACCEPT } from '../../lib/file-types';
import { cn } from '../../lib/utils';

export interface SampleAction {
	action: SampleActionType;
	label: string;
	/**
	 * The status this action moves the sample to. Null for actions that change a
	 * property without moving the sample through its lifecycle — see
	 * `MOVES_NOTHING`, which is what decides whether a null here is a bug.
	 */
	status?: OrderSampleStatus | null;
	blocked?: boolean | null;
	blockedReason?: string | null;
	/**
	 * What doing this NOW costs, in the server's words — "Starting production now
	 * means this sample has not been paid for."
	 *
	 * A warning, not a block: the action stays enabled and the mutation succeeds.
	 * The rules that used to refuse it were removed on purpose — whether to start
	 * before payment is the studio's judgement — so this must never disable
	 * anything. Nothing ever sets both this and `blockedReason`.
	 */
	warningReason?: string | null;
	requiredFields: string[];
	optionalFields: string[];
	requiresAttachments?: OrderSampleAttachmentType | null;
	supportsAttachments?: OrderSampleAttachmentType | null;
	hasAttachments?: boolean | null;
}

/**
 * Actions that carry no target status BY DESIGN.
 *
 * `kind: PROPERTY` is the server's word for "this moves nothing", and every one
 * of them comes back with `status: null`. Demanding a status of them is how
 * "This action is missing a target status" fired on a click whose upload had
 * already succeeded: one success and one failure for the same press.
 *
 * A list rather than a guess off `status == null`, because a MOVE that arrives
 * without one IS broken and the guard below should still say so. Keep this in
 * step with the backend: every PROPERTY action belongs here.
 *
 * SET_PAYMENT_TREATMENT is absent on purpose — it has its own mutation and
 * returns before any of this.
 */
export const MOVES_NOTHING = new Set<SampleActionType>([
	SampleActionType.EditDetails,
	SampleActionType.ManageVirtualDeliveryFiles,
]);

interface SampleActionModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	action: SampleAction;
	sampleId: string;
	sampleChoice: OrderSampleChoice;
	sampleRound: number;
	productName: string;
	/** Current value — seeds the SET_PAYMENT_TREATMENT picker. */
	paymentTreatment?: SamplePaymentTreatment | null;
	onSuccess: () => void;
}

export function SampleActionModal({
	open,
	onOpenChange,
	action,
	sampleId,
	sampleChoice,
	sampleRound,
	productName,
	paymentTreatment,
	onSuccess,
}: SampleActionModalProps) {
	const [updateStatus, { loading }] = useUpdateSampleStatusMutation();
	const [approveNewRound, { loading: approvingRound }] = useApproveNewSampleRoundMutation();
	const [setPaymentTreatment, { loading: settingTreatment }] =
		useSetSamplePaymentTreatmentMutation();
	const attachmentUpload = useSampleAttachmentUpload();

	const [notes, setNotes] = useState('');
	const [carrier, setCarrier] = useState('');
	const [trackingNumber, setTrackingNumber] = useState('');
	const [shippedAt, setShippedAt] = useState<Date | null>(null);
	const [actualDelivery, setActualDelivery] = useState<Date | null>(null);
	const [estimatedDelivery, setEstimatedDelivery] = useState<Date | null>(null);
	const [virtualNotes, setVirtualNotes] = useState('');

	/** Does this action declare the field at all — required or optional. */
	const owns = (field: string) =>
		action.requiredFields.includes(field) || action.optionalFields.includes(field);

	/** The carrier list the shipment form uses, so a code is stored either way. */
	const { data: carrierData } = useGetShippingCarriersQuery({ fetchPolicy: 'no-cache' });
	const carriers = carrierData?.getShippingCarriers ?? [];
	/**
	 * A new round is rework the client caused, so it is CHARGED unless someone
	 * decides otherwise — and it deliberately does NOT inherit from the round it
	 * replaces. Sending nothing would get the same default; we send it explicitly
	 * because the picker always has a value.
	 */
	const [roundTreatment, setRoundTreatment] = useState<SamplePaymentTreatment>(
		SamplePaymentTreatment.Charged
	);

	const isPhysical = sampleChoice === OrderSampleChoice.SampleWithShipping;
	const attachmentType = action.requiresAttachments || action.supportsAttachments || null;
	const needsAttachments = !!action.requiresAttachments && !action.hasAttachments;
	const canAttach = !!attachmentType;
	const isBusy = loading || approvingRound || settingTreatment || attachmentUpload.uploading;

	// A picker, not a toggle: three states can't be inverted, so the mutation takes
	// the value chosen rather than the opposite of the current one.
	const isTreatmentAction = action.action === SampleActionType.SetPaymentTreatment;
	const [treatment, setTreatment] = useState<SamplePaymentTreatment>(
		paymentTreatment ?? SamplePaymentTreatment.Charged
	);

	const resetForm = () => {
		setNotes('');
		setCarrier('');
		setTrackingNumber('');
		setEstimatedDelivery(null);
		setVirtualNotes('');
		setRoundTreatment(SamplePaymentTreatment.Charged);
		attachmentUpload.reset();
	};

	const close = () => {
		onOpenChange(false);
		resetForm();
	};

	const handleSubmit = async () => {
		if (needsAttachments && attachmentUpload.pendingFiles.length === 0) {
			toast.error('Please upload at least one file');
			return;
		}

		if (canAttach && attachmentUpload.pendingFiles.length > 0) {
			const success = await attachmentUpload.uploadAll(sampleId, attachmentType!);
			if (!success) return;
		}

		if (action.action === SampleActionType.ApproveNewRound) {
			try {
				const { data } = await approveNewRound({
					variables: {
						id: sampleId,
						input: {
							notes: notes.trim() || undefined,
							paymentTreatment: roundTreatment,
						},
					},
				});
				const error = getMutationError(data?.approveNewSampleRound);
				if (error) {
					toast.error(error);
					return;
				}
				toast.success(action.label);
				close();
				onSuccess();
			} catch {
				toast.error('Failed to approve new round');
			}
			return;
		}

		if (isTreatmentAction) {
			try {
				const { data } = await setPaymentTreatment({
					variables: { sampleId, treatment },
				});
				// The mutation re-checks with the payment service and is the
				// authority — a stale tab can still be refused.
				const error = getMutationError(data?.setSamplePaymentTreatment);
				if (error) {
					toast.error(error);
					return;
				}
				toast.success(action.label);
				close();
				onSuccess();
			} catch {
				toast.error('Failed to change sample payment');
			}
			return;
		}

		// Attachments-only, and the attachments are already saved above.
		//
		// The affordance exists to say uploading is still ALLOWED while the round
		// sits awaiting review — it owns no fields and no transition, so there is
		// nothing left to send. Routing it through updateSampleStatus was the bug:
		// a no-status call resolves to EDIT_DETAILS server-side, so the page was
		// asking for a different action than the one that was clicked.
		if (action.action === SampleActionType.ManageVirtualDeliveryFiles) {
			close();
			onSuccess();
			return;
		}

		if (!action.status && !MOVES_NOTHING.has(action.action)) {
			toast.error('This action is missing a target status');
			return;
		}

		// Status OMITTED, not null, on an action that moves nothing — the mutation
		// gates on the field being PRESENT, and a null would read as a transition
		// to nowhere rather than as an edit.
		const input: UpdateSampleStatusInput = action.status ? { status: action.status } : {};

		// Gated on the action's OWN fields, not on one of them standing in for the
		// rest. The ship transitions declare carrier and trackingNumber required,
		// estimatedDeliveryDate optional — the same set, in the same order, as the
		// shipment form — so each is read from the list that names it.
		if (owns('trackingNumber')) {
			if (!trackingNumber.trim()) {
				toast.error('Tracking number is required');
				return;
			}
			input.trackingNumber = trackingNumber.trim();
		}
		if (owns('carrier')) {
			if (action.requiredFields.includes('carrier') && !carrier.trim()) {
				toast.error('Carrier is required');
				return;
			}
			input.carrier = carrier.trim() || undefined;
		}
		// Left out when the studio didn't set one, so the server applies its own
		// default of now. Sending null would be a claim; omitting it is not.
		if (owns('shippedAt')) {
			input.shippedAt = shippedAt?.toISOString() || undefined;
		}
		if (owns('estimatedDeliveryDate')) {
			input.estimatedDeliveryDate = estimatedDelivery?.toISOString() || undefined;
		}
		// Same rule as shippedAt: omitted when unset, so the server's own "now"
		// applies. A sample marked delivered the morning after it landed records
		// the wrong arrival otherwise — and that date feeds the Deliveries rail.
		if (owns('actualDeliveryDate')) {
			input.actualDeliveryDate = actualDelivery?.toISOString() || undefined;
		}
		if (action.requiredFields.includes('virtualDeliveryNotes') || virtualNotes.trim()) {
			input.virtualDeliveryNotes = virtualNotes.trim() || undefined;
		}
		// Only when the action advertises it. The backend writes approvalNotes
		// solely on the Approve transition, so sending it on any other one was
		// collecting text and dropping it.
		if (action.optionalFields.includes('approvalNotes') && notes.trim()) {
			input.approvalNotes = notes.trim();
		}

		try {
			const { data } = await updateStatus({
				variables: { id: sampleId, input },
			});
			const error = getMutationError(data?.updateSampleStatus);
			if (error) {
				toast.error(error);
				return;
			}
			toast.success(action.label);
			close();
			onSuccess();
		} catch {
			toast.error('Failed to update sample');
		}
	};

	return (
		<Dialog open={open} onOpenChange={(v) => !v && close()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{action.label}</DialogTitle>
					<DialogDescription>
						{productName} — {isPhysical ? 'Physical' : 'Virtual'} Sample
						{sampleRound > 1 && ` (Round ${sampleRound})`}
					</DialogDescription>
				</DialogHeader>
				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							{/* What this costs, said before it is true. On the confirm
							    rather than beside the button, because this is the last
							    moment it can be reconsidered — and never as a disabled
							    state, which would put back the rule the backend removed
							    on purpose. */}
							{action.warningReason && (
								<div className="flex gap-2 rounded-[var(--r-2)] border border-line-2 bg-surface-sunken px-3 py-2.5">
									<TriangleAlert
										size={14}
										className="mt-px shrink-0 text-status-warn"
										aria-hidden
									/>
									<span className="text-xs leading-relaxed text-text-2">
										{action.warningReason}
									</span>
								</div>
							)}
							{/* Shipping a sample asks for what shipping a box asks for.
							    Both declare the same fields, and this form is laid out in
							    the shipment form's own order — carrier, tracking number,
							    shipped, est. delivery — because the two sit side by side
							    in a studio's day and a swapped first field is the thing
							    that makes them feel like different tools.
							    It had drifted further than order: carrier was optional
							    free text with no picker, and both fields hung off
							    `trackingNumber` being required rather than off their own
							    entry in the action's lists. */}
							{owns('carrier') && (
								<Field
									label="Carrier"
									required={action.requiredFields.includes('carrier')}
								>
									<Input
										value={carrier}
										onChange={(e) => setCarrier(e.target.value)}
									/>
									{/* The picker is not decoration: `trackingUrl`
									    resolves from a stored carrier CODE, and free
									    text never matches one — type "DHL Express" and
									    the link stays null. Same list the shipment form
									    offers. */}
									{carriers.length > 0 && (
										<div className="mt-1.5 flex flex-wrap gap-1.5">
											{carriers.map((c) => {
												const active =
													carrier.trim().toLowerCase() ===
													c.code.toLowerCase();
												return (
													<button
														key={c.code}
														type="button"
														onClick={() => setCarrier(c.code)}
														title={
															c.supportsTracking
																? 'A tracking link resolves for this carrier'
																: 'No tracking page for this carrier'
														}
														className={cn(
															'h-6 rounded-md border px-2 text-xs',
															active ? 'text-text-1' : 'text-text-3'
														)}
														style={{
															borderColor: active
																? 'var(--accent)'
																: 'var(--line-2)',
															background: active
																? 'var(--accent-soft)'
																: 'transparent',
														}}
													>
														{c.name}
													</button>
												);
											})}
										</div>
									)}
								</Field>
							)}
							{owns('trackingNumber') && (
								<Field label="Tracking number" required>
									<Input
										value={trackingNumber}
										onChange={(e) => setTrackingNumber(e.target.value)}
									/>
								</Field>
							)}
							{/* Before "Est. delivery", as on the shipment form — a
							    dispatch is read before the arrival it predicts.
							    No `minDate`: this records when it actually WENT, which
							    is in the past for anything entered the day after. Left
							    empty the server applies now, which is exactly the case
							    the field exists to correct. */}
							{owns('shippedAt') && (
								<Field label="Shipped">
									<DatePicker
										value={shippedAt}
										onChange={(d) => setShippedAt(d ?? null)}
										placeholder="Pick a date"
										clearable
									/>
								</Field>
							)}
							{owns('estimatedDeliveryDate') && (
								<Field label="Est. delivery">
									<DatePicker
										value={estimatedDelivery}
										onChange={(d) => setEstimatedDelivery(d ?? null)}
										placeholder="Pick a date"
										minDate={new Date()}
									/>
								</Field>
							)}
							{/* Last, as on the shipment form. No `minDate`: an arrival
							    recorded the day after is in the past, which is the whole
							    reason the field exists.

							    A VIRTUAL submission never shows this — it declares
							    `virtualDeliveryNotes` instead, because submitting files
							    happens at the moment you submit and there is nothing to
							    backdate. No special case needed here: the fields render
							    from what the action declares, so the two kinds of
							    "delivered" differ by themselves. */}
							{owns('actualDeliveryDate') && (
								<Field label="Delivered">
									<DatePicker
										value={actualDelivery}
										onChange={(d) => setActualDelivery(d ?? null)}
										placeholder="Pick a date"
										clearable
									/>
								</Field>
							)}

							{/* Virtual delivery notes */}
							{owns('virtualDeliveryNotes') && (
								<Field label="Notes">
									<Textarea
										value={virtualNotes}
										onChange={(e) => setVirtualNotes(e.target.value)}
										placeholder="Any notes about the virtual sample..."
										rows={3}
									/>
								</Field>
							)}

							{/* The order-side change: a picker seeded with the current
							    value, since three states can't be toggled. */}
							{isTreatmentAction && (
								<SampleTreatmentPicker value={treatment} onChange={setTreatment} />
							)}

							{/* Offered only when the action actually stores one, rather
							    than on everything without a tracking or delivery field —
							    which put a textarea on transitions that discard it. */}
							{(action.optionalFields.includes('approvalNotes') ||
								action.optionalFields.includes('notes')) && (
								<Field label="Notes (optional)">
									<Textarea
										value={notes}
										onChange={(e) => setNotes(e.target.value)}
										placeholder="Any notes..."
										rows={3}
									/>
								</Field>
							)}

							{/* Greenlight: how the NEW round is paid for. Same three
							    options as the offer, so an admin doesn't learn two
							    vocabularies for one decision. */}
							{action.optionalFields.includes('paymentTreatment') && (
								<SampleTreatmentPicker
									label="Charging for this round"
									value={roundTreatment}
									onChange={setRoundTreatment}
								/>
							)}

							{/* File upload */}
							{canAttach && (
								<div>
									<div className="mb-1.5 text-sm font-medium text-text-1">
										Attachments{' '}
										{needsAttachments && (
											<span className="text-destructive">*</span>
										)}
									</div>
									<FileInput
										onFiles={(files) => attachmentUpload.addFiles(files)}
										accept={SAMPLE_MEDIA_ACCEPT}
										multiple
									>
										{({ open: openPicker, converting }) => (
											<div
												onClick={openPicker}
												className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed border-line-2 px-4 py-5 text-center transition-colors hover:border-line-3 hover:bg-surface-hover"
											>
												<Upload size={16} className="text-text-4" />
												<div className="text-sm text-text-3">
													{converting
														? 'Converting...'
														: 'Click to upload files'}
												</div>
												<div className="text-xs text-text-4">
													Images, videos, PDFs
												</div>
											</div>
										)}
									</FileInput>

									{attachmentUpload.pendingFiles.length > 0 && (
										<div className="mt-2 flex flex-col gap-1">
											{attachmentUpload.pendingFiles.map((f, i) => (
												<div
													key={i}
													className="flex items-center gap-2 rounded-md bg-surface-fill px-2.5 py-1.5 text-xs"
												>
													{f.preview ? (
														<img
															src={f.preview}
															alt=""
															className="h-7 w-7 shrink-0 rounded object-cover"
														/>
													) : (
														<FileTextIcon
															size={14}
															className="shrink-0 text-text-4"
														/>
													)}
													<span className="min-w-0 flex-1 truncate text-text-2">
														{f.file.name}
													</span>
													<span className="shrink-0 text-text-4">
														{(f.file.size / 1024).toFixed(0)} KB
													</span>
													<button
														type="button"
														onClick={() =>
															attachmentUpload.removeFile(i)
														}
														className="shrink-0 rounded p-0.5 text-text-4 transition-colors hover:text-text-1"
													>
														<Trash2 size={11} />
													</button>
												</div>
											))}
										</div>
									)}
								</div>
							)}
						</div>
					</DialogBody>
					<DialogFooter>
						<Button type="button" variant="outline" onClick={close} disabled={isBusy}>
							Cancel
						</Button>
						<Button
							type="submit"
							disabled={
								isBusy ||
								(needsAttachments && attachmentUpload.pendingFiles.length === 0)
							}
						>
							{isBusy ? 'Processing...' : action.label}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
